/**
 * 주문 취소 관련 로직
 * 기존 order.ts에서 분리
 */

import { supabase } from '@/lib/supabase';
import type { Order } from '@/types';
import { updateOrderStatus, sendOrderNotification } from './order-status';

/**
 * 주문 ID로 주문 조회 (내부 사용)
 */
async function getOrderById(orderId: string): Promise<Order | null> {
  try {
    const { data, error } = await supabase
      .from('orders')
      .select(`
        *,
        items:order_items(
          *,
          product:products!product_id(id, name, original_price),
          offer:product_offers!offer_id(id, offer_type, discount_rate, discounted_price)
        ),
        restaurant:restaurants(id, name, address, phone),
        payment:payments(id, amount, status, transaction_id, paid_at, approved_at)
      `)
      .eq('id', orderId)
      .single();

    if (error || !data) {
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error fetching order:', error);
    return null;
  }
}

/**
 * 주문 취소
 */
export async function cancelOrder(
  orderId: string,
  reason?: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // Get order and payment details
    const order = await getOrderById(orderId);
    if (!order) {
      return { success: false, error: '주문을 찾을 수 없습니다' };
    }

    // 이미 취소된 주문인지 확인
    if (order.status === 'CANCELLED') {
      return { success: false, error: '이미 취소된 주문입니다' };
    }

    // Check if cancellation is allowed based on order type
    if (order.order_type === 'DEADLINE_DISCOUNT') {
      // 마감할인: 매장 수락(ACCEPTED) 전까지 취소 가능
      if (order.status !== 'PENDING') {
        return { success: false, error: '매장이 수락한 주문은 취소할 수 없습니다' };
      }
    } else if (order.order_type === 'RESERVATION_DISCOUNT') {
      // 예약할인은 수령시간 2시간 전까지만 취소 가능
      if (order.pickup_time) {
        const pickupTime = new Date(order.pickup_time).getTime();
        const now = new Date().getTime();
        const hoursUntilPickup = (pickupTime - now) / (1000 * 60 * 60);
        
        if (hoursUntilPickup < 2) {
          return { success: false, error: '예약할인 주문은 수령시간 2시간 전까지만 취소 가능합니다' };
        }
      }
    }

    // Handle payment cancellation if payment was completed
    if (order.payment_status === 'PAID') {
      // 구매자 취소도 판매자 취소와 동일하게 process-refund 사용
      try {
        const { data, error } = await supabase.functions.invoke('process-refund', {
          body: {
            orderId,
            reason: reason || '고객 요청 취소',
            isAutoCancel: false
          }
        });

        if (error) {
          console.error('process-refund error:', error);
          return { success: false, error: '환불 처리 중 오류가 발생했습니다' };
        }

        // idempotent: 이미 환불된 경우도 성공 처리
        if ((data as any)?.success !== false) {
          // 알림 발송 (실패해도 무시)
          sendOrderNotification(orderId, 'CANCELLED').catch(console.error);
          return { success: true };
        }

        return { success: false, error: (data as any)?.error || '환불 처리에 실패했습니다' };
      } catch (err) {
        console.error('process-refund invoke failed:', err);
        return { success: false, error: '환불 처리 중 오류가 발생했습니다' };
      }
    }

    // Update order status (updateOrderStatus가 자동으로 알림 발송)
    const statusResult = await updateOrderStatus(orderId, 'CANCELLED');
    if (!statusResult.success) {
      return statusResult;
    }

    return { success: true };
  } catch (error) {
    console.error('Error cancelling order:', error);
    return { success: false, error: '주문 취소에 실패했습니다.' };
  }
}

/**
 * 주문 취소 가능 여부 확인
 */
export async function canCancelOrder(
  orderId: string,
  userId: string
): Promise<{ canCancel: boolean; reason?: string }> {
  try {
    const order = await getOrderById(orderId);
    
    if (!order) {
      return { canCancel: false, reason: '주문을 찾을 수 없습니다.' };
    }

    if (order.buyer_id !== userId) {
      return { canCancel: false, reason: '본인의 주문만 취소할 수 있습니다.' };
    }

    // 이미 취소되거나 완료된 주문은 취소 불가
    if (order.status === 'CANCELLED' || order.status === 'COMPLETED' || order.status === 'NOSHOW') {
      return { canCancel: false, reason: '이미 처리된 주문입니다.' };
    }

    const now = new Date();

    switch (order.order_type) {
      case 'REGULAR_ORDER':
        // PENDING 상태에서만 취소 가능
        if (order.status === 'PENDING') {
          return { canCancel: true };
        }
        return { canCancel: false, reason: '주문이 수락된 후에는 취소할 수 없습니다.' };

      case 'DEADLINE_DISCOUNT':
        // READY 상태면 취소 불가
        if (order.status === 'READY') {
          return { canCancel: false, reason: '준비 완료된 주문은 취소할 수 없습니다.' };
        }
        // ACCEPTED 상태면 취소 불가
        if (order.status === 'ACCEPTED') {
          return { canCancel: false, reason: '주문이 수락된 후에는 취소할 수 없습니다.' };
        }
        // PENDING이면 시간 제한 없이 취소 가능
        if (order.status === 'PENDING') {
          return { canCancel: true };
        }
        return { canCancel: false, reason: '주문 상태상 취소할 수 없습니다.' };

      case 'RESERVATION_DISCOUNT':
        // READY 상태면 취소 불가 (최우선 조건)
        if (order.status === 'READY') {
          return { canCancel: false, reason: '준비 완료된 주문은 취소할 수 없습니다.' };
        }
        
        // PENDING 상태(첫 10분)는 취소 가능
        if (order.status === 'PENDING') {
          return { canCancel: true };
        }
        
        // ACCEPTED 상태에서는 픽업 시간 2시간 전까지만 취소 가능
        if (order.pickup_time) {
          const pickupTime = new Date(order.pickup_time);
          const hoursUntilPickup = (pickupTime.getTime() - now.getTime()) / (1000 * 60 * 60);
          
          if (hoursUntilPickup >= 2) {
            return { canCancel: true };
          }
          return { canCancel: false, reason: '픽업 시간 2시간 전까지만 취소 가능합니다.' };
        }
        return { canCancel: false, reason: '픽업 시간이 설정되지 않았습니다.' };

      default:
        return { canCancel: false, reason: '알 수 없는 주문 유형입니다.' };
    }
  } catch (error) {
    console.error('Error checking order cancellation:', error);
    return { canCancel: false, reason: '취소 가능 여부 확인 중 오류가 발생했습니다.' };
  }
}

/**
 * 실패한 결제 주문 정리 (15분 이상 경과한 PENDING 주문이면서 payment_key가 없는 경우)
 */
export async function cleanupFailedPaymentOrders(): Promise<{ success: boolean; cleanedCount: number; error?: string }> {
  try {
    console.log('Starting cleanup of failed payment orders...');
    
    // 15분 이상 경과한 PENDING 상태의 주문 중 payment_key가 없는 것들 조회
    const cutoffTime = new Date(Date.now() - 15 * 60 * 1000).toISOString();
    
    const { data: failedOrders, error: queryError } = await supabase
      .from('orders')
      .select(`
        id,
        order_number,
        created_at,
        cart_id,
        payment:payments(id, payment_key)
      `)
      .eq('status', 'PENDING')
      .eq('payment_status', 'PENDING')
      .lt('created_at', cutoffTime);

    if (queryError) {
      console.error('Error querying failed orders:', queryError);
      return { success: false, cleanedCount: 0, error: queryError.message };
    }

    if (!failedOrders || failedOrders.length === 0) {
      console.log('No failed payment orders found for cleanup');
      return { success: true, cleanedCount: 0 };
    }

    // payment_key가 없는 주문들만 필터링
    const ordersToCleanup = failedOrders.filter(order => {
      const payment = order.payment as any;
      return !payment?.payment_key;
    });

    if (ordersToCleanup.length === 0) {
      console.log('No orders need cleanup (all have payment_key)');
      return { success: true, cleanedCount: 0 };
    }

    console.log(`Found ${ordersToCleanup.length} failed payment orders to cleanup`);
    
    let cleanedCount = 0;

    for (const order of ordersToCleanup) {
      try {
        // 주문 상태를 CANCELLED, 결제 상태를 FAILED로 변경
        const { error: orderUpdateError } = await supabase
          .from('orders')
          .update({ 
            status: 'CANCELLED',
            payment_status: 'FAILED',
            updated_at: new Date().toISOString()
          })
          .eq('id', order.id);

        if (orderUpdateError) {
          console.error(`Failed to update order ${order.order_number}:`, orderUpdateError);
          continue;
        }

        // 결제 레코드가 있다면 상태 업데이트
        if (order.payment) {
          await supabase
            .from('payments')
            .update({
              status: 'failed',
              failure_reason: '결제 승인 타임아웃',
              updated_at: new Date().toISOString()
            })
            .eq('id', (order.payment as any).id);
        }

        // 장바구니가 있다면 상태를 active로 복원 (다시 사용할 수 있도록)
        if (order.cart_id) {
          await supabase
            .from('carts')
            .update({
              status: 'active',
              updated_at: new Date().toISOString()
            })
            .eq('id', order.cart_id);
        }

        cleanedCount++;
        console.log(`Cleaned up failed order: ${order.order_number}`);

      } catch (error) {
        console.error(`Error cleaning up order ${order.order_number}:`, error);
        continue;
      }
    }

    console.log(`Cleanup completed. ${cleanedCount}/${ordersToCleanup.length} orders cleaned up.`);
    
    return { 
      success: true, 
      cleanedCount,
      error: cleanedCount < ordersToCleanup.length ? `Partially completed: ${cleanedCount}/${ordersToCleanup.length}` : undefined
    };
    
  } catch (error) {
    console.error('Error during failed payment cleanup:', error);
    return { 
      success: false, 
      cleanedCount: 0, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}
